import React, { useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { SortOrder } from "@common/constants";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { getTransactions } from "@api/transaction.api";
import {
    TransactionSort,
    TransactionsQuerySortField,
} from "@common/schemas/transaction.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { useEntities } from "@hooks/useEntities";
import { useEffectiveBookkeepingStartDate } from "@hooks/useEffectiveBookkeepingStartDate";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Loader } from "@design-system/Loader/Loader";
import { useCreateLinkFinancialDocumentToFinancialTransactionMutation } from "@mutations/financialDocument";
import { useClasses } from "@api/class.api";
import { transactionMatchingQueryKeys } from "../../lib";
import { convertFiltersToDto } from "../../../transactions/useTransactionsQuery";
import { TransactionsFilters } from "../../../transactions/filters/lib";
import { NoMatchingTransactions } from "../../../transactions/NoMatchingTransactions";
import { FinancialDocumentDetailsView } from "../usePathBasedFinancialDocumentDetailsView";
import { queryClient } from "../../../../queryClient";
import { TablePageHeader } from "../../../pageHeader/TablePageHeader";
import { FilterTabs } from "../../../pageHeader/filters/lib";
import { useFinancialDocumentDetailsContext } from "../financialDocumentDetails.context";
import styles from "./MatchingView.module.scss";
import { MatchingViewTransaction } from "./MatchingViewTransaction";

interface Props {
    financialDocument: FinancialDocumentExtended;
    filters: TransactionsFilters;
    onFiltersChange: (filters: TransactionsFilters) => void;
    clearFilters: () => void;
}

const FIXED_SORT: TransactionSort = {
    field: TransactionsQuerySortField.DATE,
    order: SortOrder.DESC,
};

export const MatchingView: React.FC<Props> = ({
    financialDocument,
    filters,
    onFiltersChange,
    clearFilters,
}) => {
    const { changeView } = useFinancialDocumentDetailsContext();
    const workspace = useActiveWorkspace();
    const match = useCreateLinkFinancialDocumentToFinancialTransactionMutation(
        financialDocument,
        () => changeView(FinancialDocumentDetailsView.DEFAULT),
    );
    const entities = useEntities();
    const bookkeepingStartDate = useEffectiveBookkeepingStartDate(
        filters.entityIds,
    );
    const { isActivated: classesEnabled } = useClasses();

    const filtersDto = useMemo(
        () => convertFiltersToDto(filters, entities),
        [filters, entities],
    );

    useEffect(() => {
        queryClient.removeQueries({
            queryKey: transactionMatchingQueryKeys.list(filtersDto),
        });
        // remove previously fetched data when entering matching
    }, [filtersDto]);

    const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
        queryKey: transactionMatchingQueryKeys.list(filtersDto),
        queryFn: ({ pageParam = 0 }) =>
            getTransactions({
                filters: filtersDto,
                sort: FIXED_SORT,
                limit: 20,
                page: pageParam,
                workspaceId: workspace?.id ?? "",
            }),
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.pageCount > allPages.length) {
                return allPages.length;
            }
        },
        initialPageParam: 0,
    });

    const { inView, ref } = useInView({
        initialInView: true,
    });

    useEffect(() => {
        if (inView && hasNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, inView]);

    let content;

    if (!data) {
        content = (
            <tr>
                <td colSpan={99}>
                    <Loader />
                </td>
            </tr>
        );
    } else if (data.pages[0].data.length === 0) {
        content = (
            <tr>
                <td colSpan={99}>
                    <NoMatchingTransactions
                        onResetFilters={clearFilters}
                        bookkeepingStartDate={bookkeepingStartDate}
                    />
                </td>
            </tr>
        );
    } else {
        content = data.pages
            .flatMap((page) => [...page.data])
            .map((transaction) => (
                <MatchingViewTransaction
                    financialDocument={financialDocument}
                    transaction={transaction}
                    key={transaction.id}
                    onMatch={() => match.mutateAsync(transaction.id)}
                />
            ));
    }

    return (
        <main className={styles.container}>
            <TablePageHeader
                search={filters.search}
                onSearchChange={(value) => {
                    onFiltersChange({ search: value ?? "" });
                }}
                filters={filters}
                onFiltersChange={onFiltersChange}
                availableFilters={[
                    FilterTabs.DATE,
                    FilterTabs.CATEGORY,
                    FilterTabs.ACCOUNT,
                    ...(classesEnabled ? [FilterTabs.CLASS] : []),
                    FilterTabs.AMOUNT,
                    FilterTabs.RULES,
                    FilterTabs.COUNTERPARTY,
                    FilterTabs.NOTE,
                    FilterTabs.RECEIPTS,
                    FilterTabs.SPLIT,
                    FilterTabs.MATCHED,
                ]}
            >
                <EntityFilterCombobox
                    filters={filters}
                    onChange={onFiltersChange}
                    excludeEmptyMockEntities={
                        ExcludeEmptyMockEntitiesMode.REQUIRE_TRANSACTIONS
                    }
                />
            </TablePageHeader>

            <section className={styles.transactions}>
                <table className="table table-borderless">
                    <thead>
                        <tr>
                            <th aria-label="Lock"></th>
                            <th>Date</th>
                            <th>Description</th>
                            <th className="text-right">Account</th>
                            <th className="text-right">Amount</th>
                            <th aria-label="Entity"></th>
                            <th aria-label="Match"></th>
                        </tr>
                    </thead>

                    <tbody>{content}</tbody>

                    <footer
                        className={styles.observerFooter}
                        ref={ref}
                    ></footer>
                </table>
            </section>
        </main>
    );
};
